import { useState, useEffect } from "react";
import {
    Table,
    Spin,
    Select,
} from "antd";
import CommonObj from "../../CommonObj";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import { renderCommonPaginationTotal } from "../../components/commonpagination/common-pagination";
import "../../components/commonpagination/common-pagination.css";
import { buildTimestampedFileName } from "../../components/cstDateFileName";
import { message } from "../../components/antdMessageBridge";
import { exportToCSV } from "../../components/exportToCsv";

const { Option } = Select;

const StagnantBooksQueue = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchFilters, setsearchFilters] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [statusOptions, setStatusOptions] = useState([]);
    const [selectedStatusId, setSelectedStatusId] = useState(["0"]);
    const [selectedSubStatusId, setSelectedSubStatusId] = useState(["0"]);
    const [apiNoResults, setApiNoResults] = useState(false);
    const [disableExport, setDisableExport] = useState(false);
    const { accessToken } = useAuthToken();
    const [isVisible, setIsVisible] = useState(true);

    const handleCleardate = () => {
        setData([]);
        setSelectedStatusId(["0"]);
        setSelectedSubStatusId(["0"]);
        setApiNoResults(false);
        setDisableExport(false);
        setPagination((prev) => ({
            ...prev,
            current: 1,
            total: 0,
        }));
    };

    const fetchStatusData = async () => {
        setLoading(true);
        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
            };
            const endpoint =
                CommonObj?.Endpoints?.GetBIDsWithStatusFilter ||
                "/api/Report/GetBIDsWithStatusFilter";
            const response = await fetch(endpoint, {
                method: "GET",
                headers: myHeaders,
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const result = await response.json();
            const statuses = Array.isArray(result)
                ? result
                : Array.isArray(result?.data)
                ? result.data
                : Array.isArray(result?.items)
                ? result.items
                : [];
            setStatusOptions(statuses);
        } catch (err) {
            console.log("Fetch status filter data error:", err);
        } finally {
            setLoading(false);
        }
    };

    const uniqueStatusOptions = Array.from(
        new Map(statusOptions.map((item) => [item.statusID, item])).values()
    );

    const uniqueSubStatusOptions = Array.from(
        new Map(statusOptions.map((item) => [item.subStatusID, item])).values()
    );

    const columns = [
        {
            title: "Book ID (BID)",
            dataIndex: "bid",
            key: "bid",
            maxWidth: 220,
        },
        {
            title: "Phase",
            dataIndex: "processName",
            key: "processName",
            maxWidth: 160,
        },
        {
            title: "Current Status",
            dataIndex: "statusName",
            key: "statusName",
            maxWidth: 280,
        },
        {
            title: "Rack & Shelf",
            dataIndex: "rackandShelf",
            key: "rackandShelf",
            maxWidth: 280,
        },
        {
            title: "Stagnant Since",
            dataIndex: "statusMovedOn",
            key: "statusMovedOn",
            maxWidth: 280,
        },
        {
            title: "Time Elapsed (HHH:MM)",
            dataIndex: "timeElapsed",
            key: "timeElapsed",
            maxWidth: 280,
            sorter: true,
            sortOrder:
                sortedInfo.columnKey === "timeElapsed" ? sortedInfo.order : null,
        },
        {
            title: "Last Stagnant User",
            dataIndex: "lastStagnantUser",
            key: "lastStagnantUser",
            maxWidth: 280,
        },
    ];

    const getRowClassName = (record, index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        const normalizeFilters = {};
        Object.keys(filters).forEach((key) => {
            if (filters[key] && filters[key].length > 0) {
                normalizeFilters[key] = filters[key][0];
            }
        });

        setsearchFilters(normalizeFilters);
        setSortedInfo(sorter);

        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        fetchData({
            pagination: {
                current: newPagination.current,
                pageSize: newPagination.pageSize,
            },
            sorter,
            filters: normalizeFilters,
        });
    };

    const fetchData = async ({ pagination: pag, filters, sorter }) => {
        const selectedStatusIds = (selectedStatusId || []).filter(
            (id) => id !== "0"
        );
        const selectedSubStatusIds = (selectedSubStatusId || []).filter(
            (id) => id !== "0"
        );
        const payload = {
            pageNo: pag?.current || 1,
            pageSize: pag?.pageSize || 10,
            orderBy:
                sorter?.order === "ascend"
                    ? "ASC"
                    : sorter?.order === "descend"
                    ? "DESC"
                    : "string",
            statusID:
                selectedStatusIds.length > 0
                    ? selectedStatusIds.join(",")
                    : "0",
            subStatusID:
                selectedSubStatusIds.length > 0
                    ? selectedSubStatusIds.join(",")
                    : "0",
        };

        setLoading(true);
        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            const endpoint =
                CommonObj?.Endpoints?.GetBIDsWithStatusTimeElapsed ||
                "/api/Report/GetBIDsWithStatusTimeElapsed";
            const response = await fetch(endpoint, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });
            if (response.ok) {
                const responseText = await response.text();
                let result;

                try {
                    result = JSON.parse(responseText);
                } catch (e) {
                    if (responseText.includes("No BIDs found with status time elapsed.")) {
                        result = [];
                    } else {
                        throw new Error(
                            responseText || "Failed to parse API response."
                        );
                    }
                }

                const formattedData = Array.isArray(result)
                    ? result
                    : Array.isArray(result?.data)
                    ? result.data
                    : Array.isArray(result?.items)
                    ? result.items
                    : Array.isArray(result?.bidsWithStatusTimeElapsed)
                    ? result.bidsWithStatusTimeElapsed
                    : [];

                setData(formattedData);
                setApiNoResults(formattedData.length === 0);
                setDisableExport(formattedData?.length === 0)
                const totalFromResponse = Number(
                    result?.totalRows ??
                        result?.totalCount ??
                        result?.totalRecords ??
                        result?.recordCount ??
                        result?.count ??
                        result?.data?.[0]?.totalRows ??
                        result?.items?.[0]?.totalRows ??
                        result?.bidsWithStatusTimeElapsed?.[0]?.totalRows ??
                        formattedData?.[0]?.totalRows
                );
                const total =
                    Number.isFinite(totalFromResponse) && totalFromResponse >= 0
                        ? totalFromResponse
                        : formattedData.length || 0;
                setPagination((prev) => ({
                    ...prev,
                    current: pag?.current || prev.current,
                    pageSize: pag?.pageSize || prev.pageSize,
                    total,
                }));
            }
        } catch (err) {
            console.log("Fetch data error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadClick = async () => {
        const selectedStatusIds = (selectedStatusId || []).filter((id) => id !== "0");
        const selectedSubStatusIds = (selectedSubStatusId || []).filter((id) => id !== "0");
        const payload = {
            pageNo: 1,
            pageSize: -1,
            orderBy: sortedInfo?.order === "ascend" ? "ASC" : "DESC",
            statusID:
                selectedStatusIds.length > 0
                    ? selectedStatusIds.join(",")
                    : "0",
            subStatusID:
                selectedSubStatusIds.length > 0
                    ? selectedSubStatusIds.join(",")
                    : "0",
        };

        setLoading(true);
        try {
                const myHeaders = {
                    Authorization: `Bearer ${accessToken}`,
                    "X-CSRF-Token": encryptTimestamp(),
                    "Content-Type": "application/json",
                };
                const endpoint =
                    CommonObj?.Endpoints?.GetBIDsWithStatusTimeElapsed ||
                    "/api/Report/GetBIDsWithStatusTimeElapsed";
                const response = await fetch(endpoint, {
                    method: "POST",
                    headers: myHeaders,
                    body: JSON.stringify(payload),
                });

                const responseText = await response.text();
                let result = [];
                if (!response.ok) {
                    if (responseText.includes("No BIDs found with status time elapsed."))
                    {
                        message.error("No stagnant records found for the selected filter(s).");
                        return;
                    } else {
                        throw new Error(responseText || "Network response was not ok");
                    }
                } 
                try {
                        result = responseText.includes("No BIDs found with status time elapsed.") ? [] : JSON.parse(responseText);
                }
                catch (e) {
                    if (responseText.includes("No BIDs found with status time elapsed."))
                    {
                        message.error("No stagnant records found for the selected filter(s).");
                        return;
                    } else {
                        throw new Error( responseText || "Failed to parse API response.", { cause: e });
                    }
                }
           
            const books = Array.isArray(result) ? result : [result];
            setDisableExport(books?.length === 0);
            if (books?.length === 0) {
                message.error("No stagnant records found for the selected filter(s).");
                return;
            }
            const processedData = prepareCsvData(books);
            const fileName = buildTimestampedFileName("Stagnant_Books_Queue");
            exportToCSV(processedData, columns, fileName);
        } catch (err) {
            console.log("Fetch data error:", err);
        } finally {
            setLoading(false);
        }
    };
    const prepareCsvData = (rows) => {
        return rows.map((row) => ({
            ...row,
            statusMovedOn: row?.statusMovedOn ? `\t${row.statusMovedOn}` : "",
            timeElapsed: row?.timeElapsed ? `\t${row.timeElapsed}` : "",
        }));
    };

    useEffect(() => {
        fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });
        fetchStatusData();
    }, []);

    if (loading) {
        return (
            <Spin
                size="large"
                style={{ margin: "20px auto" }}
            />
        );
    }

    return (
        <div className="active-page-container">
            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>Stagnant Books Queue</h4>
                </div>
                <div className="active-page-header-action-container">
                    <div className="active-page-header-button-container">
                        <button
                            className="active-page-header-button"
                            onClick={() => setIsVisible(!isVisible)}
                        >
                            <span>
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M16.5 4.36133V4.87869C16.5 5.65707 16.5 6.04626 16.3053 6.3689C16.1106 6.69153 15.7549 6.89177 15.0435 7.29224L12.8587 8.52212C12.3814 8.79081 12.1427 8.92516 11.9719 9.07352C11.616 9.38245 11.3969 9.74547 11.2977 10.1907C11.25 10.4045 11.25 10.6547 11.25 11.155V13.1571C11.25 14.5892 11.25 15.3053 10.749 15.6186C10.2481 15.932 9.54359 15.6566 8.13462 15.1058C7.46346 14.8434 7.12789 14.7122 6.93894 14.4463C6.75 14.1804 6.75 13.8393 6.75 13.1571L6.75 11.155C6.75 10.6547 6.75 10.4045 6.70233 10.1907C6.60306 9.74547 6.38399 9.38245 6.02813 9.07351C5.85725 8.92516 5.6186 8.79081 5.14128 8.52212L2.95651 7.29224C2.2451 6.89177 1.8894 6.69153 1.6947 6.3689C1.5 6.04626 1.5 5.65707 1.5 4.87869V4.36133"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                    />
                                    <path
                                        opacity="0.5"
                                        d="M16.5 4.361C16.5 3.36586 16.5 2.8683 16.1705 2.55915C15.841 2.25 15.3107 2.25 14.25 2.25H3.75C2.68934 2.25 2.15901 2.25 1.8295 2.55915C1.5 2.8683 1.5 3.36586 1.5 4.361"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                    />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>
            </div>
            <div className="advance-filter-container">
                <div className="advance-filter-title-container">
                    <div className="advance-filter-title">
                        <span>
                            <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M14.7245 3.89258V4.35423C14.7245 5.04879 14.7245 5.39607 14.5508 5.68396C14.3771 5.97184 14.0597 6.15052 13.4249 6.50787L11.4754 7.6053C11.0494 7.84506 10.8365 7.96494 10.684 8.09732C10.3665 8.37299 10.171 8.69691 10.0824 9.0942C10.0399 9.28498 10.0399 9.50821 10.0399 9.95468V11.7412C10.0399 13.0191 10.0399 13.658 9.59287 13.9376C9.14585 14.2172 8.51723 13.9714 7.25999 13.48C6.66111 13.2458 6.36167 13.1288 6.19308 12.8915C6.02448 12.6542 6.02448 12.3499 6.02448 11.7412L6.02448 9.95468C6.02448 9.50821 6.02448 9.28498 5.98194 9.0942C5.89336 8.69691 5.69788 8.37299 5.38035 8.09732C5.22787 7.96494 5.01491 7.84506 4.589 7.6053L2.63951 6.50787C2.00471 6.15052 1.68731 5.97184 1.51358 5.68396C1.33984 5.39607 1.33984 5.04879 1.33984 4.35423V3.89258"
                                    stroke="#0009E2"
                                    strokeWidth="1.2"
                                />
                                <path
                                    opacity="0.5"
                                    d="M14.7245 3.89148C14.7245 3.00351 14.7245 2.55953 14.4305 2.28367C14.1365 2.00781 13.6633 2.00781 12.7168 2.00781H3.34754C2.40111 2.00781 1.92789 2.00781 1.63386 2.28367C1.33984 2.55953 1.33984 3.00351 1.33984 3.89148"
                                    stroke="#009DDB"
                                    strokeWidth="1.2"
                                />
                            </svg>
                        </span>
                        Advance Filter
                    </div>
                    <div className="advance-filter-action">
                        <button
                            style={{ display: "none" }}
                            className="active-page-header-button"
                        >
                            <span>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        opacity="0.5"
                                        cx="11.5"
                                        cy="11.5"
                                        r="7.5"
                                        stroke="currentColor"
                                        strokeWidth="1.35"
                                    />
                                    <path
                                        d="M15 12H8"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                        </button>
                        <button
                            style={{ display: "none" }}
                            className="active-page-header-button"
                        >
                            <span>
                                <svg
                                    width="25"
                                    height="25"
                                    viewBox="0 0 25 25"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="12.0311"
                                        cy="11.9999"
                                        r="7.51943"
                                        stroke="#E93548"
                                    />
                                    <path
                                        d="M13.912 10.1192L10.1523 13.8789M10.1523 10.1191L13.912 13.8788"
                                        stroke="#E93548"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>
                {isVisible && (
                    <div className="advance-filter-body-container">
                        <div className="advance-filter-input-container">
                            <div className="advance-filter-input-width w-250">
                                <div className="field-group multiple-select">
                                    <Select
                                        id="status-select"
                                        className="book-page-form-input-text has-text"
                                        showSearch
                                        allowClear
                                        mode="multiple"
                                        maxTagCount={1}
                                        maxTagPlaceholder={(omittedValues) =>
                                            `+${omittedValues.length}`
                                        }
                                        bordered={false}
                                        value={selectedStatusId}
                                        onChange={(value) => {
                                            const previousValues =
                                                selectedStatusId || [];
                                            const selectedValues =
                                                value && value.length > 0
                                                    ? value
                                                    : ["0"];
                                            if (
                                                selectedValues.includes("0") &&
                                                selectedValues.length > 1
                                            ) {
                                                const previousHasAll =
                                                    previousValues.includes("0");
                                                if (previousHasAll) {
                                                    setSelectedStatusId(
                                                        selectedValues.filter(
                                                            (v) => v !== "0"
                                                        )
                                                    );
                                                } else {
                                                    setSelectedStatusId(["0"]);
                                                }
                                                return;
                                            }
                                            setSelectedStatusId(selectedValues);
                                            setDisableExport(false);
                                        }}
                                        filterOption={(input, option) =>
                                            (option?.children ?? "")
                                                .toLowerCase()
                                                .includes(input.toLowerCase())
                                        }
                                        style={{ width: "100%" }}
                                    >
                                        <Option value="0">All</Option>
                                        {uniqueStatusOptions.map((option) => (
                                            <Option
                                                key={option.statusID}
                                                value={String(option.statusID)}
                                            >
                                                {option.currentStatus}
                                            </Option>
                                        ))}
                                    </Select>
                                    <label htmlFor="status-select">
                                        Phase{" "}
                                        <span className="form-required">*</span>
                                    </label>
                                </div>
                            </div>
                            <div
                                className="advance-filter-input-width w-250"
                                style={{ width: "300px" }}
                            >
                                <div className="field-group multiple-select">
                                    <Select
                                        id="substatus-select"
                                        className="book-page-form-input-text has-text"
                                        showSearch
                                        allowClear
                                        mode="multiple"
                                        maxTagCount={1}
                                        maxTagPlaceholder={(omittedValues) =>
                                            `+${omittedValues.length}`
                                        }
                                        bordered={false}
                                        value={selectedSubStatusId}
                                        onChange={(value) => {
                                            const previousValues =
                                                selectedSubStatusId || [];
                                            const selectedValues =
                                                value && value.length > 0
                                                    ? value
                                                    : ["0"];
                                            if (
                                                selectedValues.includes("0") &&
                                                selectedValues.length > 1
                                            ) {
                                                const previousHasAll =
                                                    previousValues.includes("0");
                                                if (previousHasAll) {
                                                    setSelectedSubStatusId(
                                                        selectedValues.filter(
                                                            (v) => v !== "0"
                                                        )
                                                    );
                                                } else {
                                                    setSelectedSubStatusId([
                                                        "0",
                                                    ]);
                                                }
                                                return;
                                            }
                                            setSelectedSubStatusId(
                                                selectedValues
                                            );
                                            setDisableExport(false);
                                        }}
                                        filterOption={(input, option) =>
                                            (option?.children ?? "")
                                                .toLowerCase()
                                                .includes(input.toLowerCase())
                                        }
                                        style={{ width: "100%" }}
                                    >
                                        <Option value="0">All</Option>
                                        {uniqueSubStatusOptions.map((option) => (
                                            <Option
                                                key={option.subStatusID}
                                                value={String(
                                                    option.subStatusID
                                                )}
                                            >
                                                {option.subStatus}
                                            </Option>
                                        ))}
                                    </Select>
                                    <label htmlFor="substatus-select">
                                        Current Status{" "}
                                        <span className="form-required">*</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="advance-filter-button-container">
                            <button
                                className="cancel-button-gray-outline"
                                onClick={handleCleardate}
                            >
                                Clear
                            </button>
                            <button
                                className="submit-button-gradiant-outline"
                                data-track="stagnant-books-queue-generate"
                                onClick={(e) => {
                                    e.preventDefault();
                                    const resetPagination = {
                                        ...pagination,
                                        current: 1,
                                    };
                                    setPagination(resetPagination);
                                    fetchData({
                                        pagination: resetPagination,
                                        sorter: sortedInfo,
                                        filters: searchFilters,
                                    });
                                }}
                            >
                                Generate
                            </button>
                            <button className={disableExport ? "submit-button-gradiant-outline disabled_button" :"submit-button-gradiant-outline" }
                                onClick={handleDownloadClick}
                                disabled={loading || disableExport}
                            >
                                {loading ? 'Processing...' : 'Export to CSV'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <div className="active-page-body-container">
                <div className="rounded-table-wrapper">
                    <Table
                        columns={columns}
                        dataSource={data}
                        className="common-pagination common-grid"
                        locale={{
                            emptyText: apiNoResults
                                ? "No stagnant records found for the selected filter(s)."
                                : "No stagnant Data available.",
                        }}
                        pagination={{
                            ...pagination,
                              pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                            showSizeChanger: true,
                            position: ["bottomRight"],
                            defaultPageSize: 10,
                            showTotal: (total, range) =>
                                renderCommonPaginationTotal(total, range, {
                                    pagination,
                                    handleTableChange,
                                    searchFilters,
                                    sortedInfo
                                }),
                        }}
                        rowKey={(record) => record.bid}
                        onChange={handleTableChange}
                        scroll={{
                            x: "max-content",
                            y: "calc(100vh - 280px)",
                        }}
                        rowClassName={getRowClassName}
                    />
                </div>
            </div>
        </div>
    );
};

export default StagnantBooksQueue;